import { Opportunity } from '../types';
import { OPENAI_API_KEY } from '@env';

// Simple OpenAI + Parse approach for semantic search
interface SearchResult {
  opportunity: Opportunity;
  score: number;
}

const EMBEDDING_MODEL = 'text-embedding-3-small'; // Use small model to match existing embeddings
let openaiClient: any = null;

// Initialize OpenAI client
const getOpenAIClient = async () => {
  if (openaiClient) return openaiClient;
  
  try {
    const { default: OpenAI } = await import('openai');
    
    if (!OPENAI_API_KEY) {
      throw new Error('OpenAI API key not configured');
    }
    
    openaiClient = new OpenAI({
      apiKey: OPENAI_API_KEY,
      dangerouslyAllowBrowser: true
    });
    
    return openaiClient;
  } catch (error) {
    console.error('🤖 Failed to initialize OpenAI:', error);
    throw error;
  }
};

// Generate embedding using OpenAI
async function generateEmbedding(text: string): Promise<number[]> {
  try {
    console.log('🔧 OpenAI API call - Input text length:', text.trim().length);
    console.log('🔧 Using model:', EMBEDDING_MODEL);
    
    const client = await getOpenAIClient();
    
    const response = await client.embeddings.create({
      model: EMBEDDING_MODEL,
      input: text.trim(),
    });
    
    const embedding = response.data[0].embedding;
    console.log('🔧 OpenAI API response - Embedding dimensions:', embedding.length);
    console.log('🔧 Token usage:', response.usage?.total_tokens || 'unknown');
    
    return embedding;
  } catch (error) {
    console.error('❌ Error generating embedding:', error);
    throw error;
  }
}

// Extract text content for embedding with weighted importance
function extractOpportunityText(opportunity: Opportunity): string {
  // Give more weight to important fields by repeating them
  const titleWeight = 3; // Title appears 3 times
  const descriptionWeight = 2; // Description appears 2 times
  const organizationWeight = 2; // Organization appears 2 times
  
  const weightedParts = [
    // Heavily weight the title
    ...Array(titleWeight).fill(opportunity.title),
    
    // Weight description and organization
    ...Array(descriptionWeight).fill(opportunity.description),
    ...Array(organizationWeight).fill(opportunity.organization),
    
    // Add type and location with context
    `Type: ${opportunity.type}`,
    `Location: ${opportunity.location}`,
    
    // Add eligibility if available
    opportunity.eligibility ? `Eligibility: ${opportunity.eligibility}` : '',
    
    // Add tags with context
    ...(opportunity.tags || []).map(tag => `Tag: ${tag}`),
    
    // Add grade levels with context  
    ...(opportunity.gradeLevel || []).map(grade => `Grade: ${grade}`),
  ];
  
  return weightedParts.filter(Boolean).join(' ').trim();
}

// Store opportunity embedding in Parse
export async function storeOpportunityEmbedding(opportunity: Opportunity): Promise<boolean> {
  try {
    const Parse: any = await import('parse');
    
    // Check if embedding already exists
    const EmbeddingStore = Parse.Object.extend('OpportunityEmbedding');
    const query = new Parse.Query(EmbeddingStore);
    query.equalTo('opportunityId', opportunity.id);
    
    const existingEmbedding = await query.first();
    if (existingEmbedding) {
      // Embedding already exists, skip silently
      return true;
    }
    
    // Generate embedding
    const text = extractOpportunityText(opportunity);
    const embedding = await generateEmbedding(text);
    
    // Store in Parse
    const embeddingObj = new EmbeddingStore();
    embeddingObj.set('opportunityId', opportunity.id);
    embeddingObj.set('embedding', embedding);
    embeddingObj.set('text', text);
    embeddingObj.set('updatedAt', new Date());
    
    await embeddingObj.save();
    return true;
    
  } catch (error) {
    console.error(`🤖 Failed to store embedding for ${opportunity.id}:`, error);
    return false;
  }
}

// Get all stored embeddings from Parse
async function getAllStoredEmbeddings(): Promise<Array<{opportunityId: string, embedding: number[]}>> {
  try {
    const Parse: any = await import('parse');
    
    const EmbeddingStore = Parse.Object.extend('OpportunityEmbedding');
    const query = new Parse.Query(EmbeddingStore);
    query.limit(1000); // Adjust as needed
    
    const results = await query.find();
    console.log('💾 Raw Parse query returned', results.length, 'embedding records');
    
    const embeddings = results.map((result: any) => ({
      opportunityId: result.get('opportunityId'),
      embedding: result.get('embedding')
    }));
    
    // Validate embedding data
    const validEmbeddings = embeddings.filter((emb: {opportunityId: string, embedding: number[]}) => 
      emb.embedding && Array.isArray(emb.embedding) && emb.embedding.length > 0
    );
    
    console.log('💾 Valid embeddings after filtering:', validEmbeddings.length);
    if (validEmbeddings.length > 0) {
      console.log('💾 Sample embedding dimensions:', validEmbeddings[0].embedding.length);
    }
    
    return validEmbeddings;
    
  } catch (error) {
    console.error('❌ Failed to get stored embeddings:', error);
    return [];
  }
}

// Calculate cosine similarity
function cosineSimilarity(a: number[], b: number[]): number {
  if (a.length !== b.length) {
    console.warn(`⚠️ Dimension mismatch: Query=${a.length}D, Stored=${b.length}D`);
    return 0;
  }
  
  let dotProduct = 0;
  let normA = 0;
  let normB = 0;
  
  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  
  if (normA === 0 || normB === 0) return 0;
  return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB));
}

// Main semantic search function
export async function performSemanticSearch(
  query: string,
  opportunities: Opportunity[]
): Promise<Opportunity[]> {
  console.log('🔍 ==> SEMANTIC SEARCH STARTED');
  console.log('🔍 Query:', query);
  console.log('🔍 Available opportunities:', opportunities.length);
  
  // Show what text would be generated for first opportunity (debugging)
  if (opportunities.length > 0) {
    const sampleText = extractOpportunityText(opportunities[0]);
    console.log('🔤 Sample opportunity text extraction:');
    console.log('🔤 Title:', opportunities[0].title);
    console.log('🔤 Generated text (first 200 chars):', sampleText.substring(0, 200) + '...');
    console.log('🔤 Generated text length:', sampleText.length);
  }
  
  if (!query.trim()) {
    console.log('🔍 Empty query, returning all opportunities');
    return opportunities;
  }
  
  try {
    // Generate query embedding
    console.log('🤖 Generating query embedding...');
    const startTime = Date.now();
    const queryEmbedding = await generateEmbedding(query);
    const embeddingTime = Date.now() - startTime;
    console.log('🤖 Query embedding generated in', embeddingTime, 'ms');
    console.log('🤖 Query embedding dimensions:', queryEmbedding.length);
    console.log('🤖 Query embedding sample:', queryEmbedding.slice(0, 5), '...');
    
    // Get all stored embeddings
    console.log('💾 Fetching stored embeddings from Parse...');
    const fetchStartTime = Date.now();
    const storedEmbeddings = await getAllStoredEmbeddings();
    const fetchTime = Date.now() - fetchStartTime;
    console.log('💾 Fetched', storedEmbeddings.length, 'stored embeddings in', fetchTime, 'ms');
    
    if (storedEmbeddings.length === 0) {
      console.log('⚠️ No embeddings available, falling back to keyword search');
      return performKeywordSearch(query, opportunities);
    }
    
    // Calculate similarities
    console.log('📐 Calculating similarities...');
    const similarityStartTime = Date.now();
    const similarities: Array<{opportunityId: string, score: number}> = [];
    
    for (const stored of storedEmbeddings) {
      const similarity = cosineSimilarity(queryEmbedding, stored.embedding);
      if (similarity > 0.4) { // Lowered threshold for better results
        similarities.push({
          opportunityId: stored.opportunityId,
          score: similarity
        });
      }
    }
    
    // Log all similarities above 0.2 for debugging
    const debugSimilarities = [];
    for (const stored of storedEmbeddings) {
      const similarity = cosineSimilarity(queryEmbedding, stored.embedding);
      if (similarity > 0.2) {
        const opp = opportunities.find(o => o.id === stored.opportunityId);
        debugSimilarities.push({
          id: stored.opportunityId,
          title: opp?.title || 'Unknown',
          similarity: similarity.toFixed(3)
        });
      }
    }
    console.log('🔍 All similarities > 0.2:', debugSimilarities.slice(0, 10));
    
    const similarityTime = Date.now() - similarityStartTime;
    console.log('📐 Similarity calculation completed in', similarityTime, 'ms');
    console.log('📐 Found', similarities.length, 'opportunities with similarity > 0.5');
    
    // Sort by similarity score
    similarities.sort((a, b) => b.score - a.score);
    
    // Log top matches
    console.log('🏆 Top 10 similarity scores:');
    similarities.slice(0, 10).forEach((sim, index) => {
      const opp = opportunities.find(o => o.id === sim.opportunityId);
      console.log(`  ${index + 1}. ${sim.score.toFixed(3)} - ${opp?.title || 'Unknown'} (${sim.opportunityId})`);
    });
    
    // Map back to opportunities
    const results: Opportunity[] = [];
    for (const sim of similarities.slice(0, 20)) { // Top 20 results
      const opportunity = opportunities.find(opp => opp.id === sim.opportunityId);
      if (opportunity) {
        results.push(opportunity);
      }
    }
    
    console.log('✅ Semantic search completed');
    console.log('✅ Returning', results.length, 'results');
    console.log('✅ Result titles:', results.slice(0, 5).map(r => r.title));
    
    // Return semantic search results
    return results;
    
  } catch (error) {
    console.error('❌ Semantic search failed:', error);
    console.log('🔄 Falling back to keyword search');
    return performKeywordSearch(query, opportunities);
  }
}

// Simple keyword search fallback
function performKeywordSearch(query: string, opportunities: Opportunity[]): Opportunity[] {
  console.log('🔤 ==> KEYWORD SEARCH FALLBACK');
  console.log('🔤 Query:', query);
  
  const queryLower = query.toLowerCase();
  const queryWords = queryLower.split(/\s+/).filter(word => word.length > 2);
  console.log('🔤 Query words:', queryWords);
  
  const results = opportunities.map(opportunity => {
    const text = extractOpportunityText(opportunity).toLowerCase();
    let score = 0;
    
    // Exact phrase matching
    if (text.includes(queryLower)) {
      score += 2;
    }
    
    // Individual word matching
    queryWords.forEach(word => {
      if (text.includes(word)) {
        score += 1;
      }
    });
    
    return { opportunity, score };
  });
  
  const filteredResults = results
    .filter(result => result.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 20);
    
  console.log('🔤 Keyword search found', filteredResults.length, 'matches');
  console.log('🔤 Top 5 keyword matches:');
  filteredResults.slice(0, 5).forEach((result, index) => {
    console.log(`  ${index + 1}. Score: ${result.score} - ${result.opportunity.title}`);
  });
  
  return filteredResults.map(result => result.opportunity);
}

// Initialize embeddings for all opportunities (admin function only)
export async function initializeEmbeddings(opportunities: Opportunity[], forceRegenerate: boolean = false): Promise<void> {
  console.log(`🤖 Starting embedding initialization for ${opportunities.length} opportunities...`);
  console.log(`🤖 Force regenerate: ${forceRegenerate}`);
  console.log(`🤖 Using model: ${EMBEDDING_MODEL}`);
  
  let processed = 0;
  let skipped = 0;
  let errors = 0;
  
  for (const opportunity of opportunities) {
    try {
      if (forceRegenerate) {
        // Delete existing embedding first
        const Parse: any = await import('parse');
        const EmbeddingStore = Parse.Object.extend('OpportunityEmbedding');
        const query = new Parse.Query(EmbeddingStore);
        query.equalTo('opportunityId', opportunity.id);
        const existing = await query.first();
        if (existing) {
          await existing.destroy();
          console.log(`🗑️ Deleted old embedding for: ${opportunity.title}`);
        }
      }
      
      const success = await storeOpportunityEmbedding(opportunity);
      if (success) {
        // Check if it was actually processed or just existed
        const Parse: any = await import('parse');
        const EmbeddingStore = Parse.Object.extend('OpportunityEmbedding');
        const query = new Parse.Query(EmbeddingStore);
        query.equalTo('opportunityId', opportunity.id);
        const existing = await query.first();
        
        if (existing && existing.createdAt) {
          const createdAt = new Date(existing.createdAt);
          const now = new Date();
          const timeDiff = now.getTime() - createdAt.getTime();
          
          if (timeDiff < 60000) { // Created within last minute = newly processed
            processed++;
            console.log(`✅ Generated embedding for: ${opportunity.title}`);
          } else {
            skipped++;
          }
        }
      } else {
        errors++;
      }
      
      // Rate limiting - wait between API calls
      await new Promise(resolve => setTimeout(resolve, 100));
      
    } catch (error) {
      console.error(`❌ Error processing ${opportunity.title}:`, error);
      errors++;
    }
  }
  
  console.log(`🤖 Embedding initialization complete:`);
  console.log(`  ✅ Newly processed: ${processed}`);
  console.log(`  ⏭️ Already existed: ${skipped}`);
  console.log(`  ❌ Errors: ${errors}`);
  
  if (forceRegenerate && processed > 0) {
    console.log(`🔄 Regenerated ${processed} embeddings with improved strategy`);
  }
}



// Track search analytics
export async function trackSemanticSearch(
  query: string,
  resultsCount: number,
  clickedOpportunityId?: string
): Promise<void> {
  try {
    const analyticsData = {
      query,
      resultsCount,
      timestamp: new Date(),
      clickedOpportunityId,
    };
    
    // Search analytics tracked (see localStorage for details)
    
    // Store in localStorage
    if (typeof localStorage !== 'undefined') {
      const existing = localStorage.getItem('searchAnalytics');
      const analytics = existing ? JSON.parse(existing) : [];
      analytics.push(analyticsData);
      
      if (analytics.length > 50) {
        analytics.splice(0, analytics.length - 50);
      }
      
      localStorage.setItem('searchAnalytics', JSON.stringify(analytics));
    }
  } catch (error) {
    console.error('Error tracking search:', error);
  }
} 